/**
 * Service API pour le suivi des enveloppes et facturation correction
 */
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithHeaders } from './baseApi';

export interface Enveloppe {
  id: number;
  reference: string;
  releve_correction: number | null;
  releve_code: string;
  matiere_nom: string;
  matiere_code: string;
  classes: string;
  semestre?: string;
  type_examen?: 'normal' | 'rattrapage';
  formateur_nom: string;
  formateur_id: number | null;
  nombre_copies: number;
  annee_academique: string;
  statut: 'prete' | 'retiree' | 'en_correction' | 'deposee' | 'facturee';
  statut_display: string;
  date_creation: string;
  date_retrait: string | null;
  date_depot: string | null;
  date_facturation: string | null;
  retire_par_nom: string | null;
  depose_aupres_de_nom: string | null;
  notes: string;
  duree_correction: string | null;
}

export interface RecuCorrection {
  id: number;
  numero_recu: string;
  formateur_nom: string;
  formateur_id: number | null;
  type_examen?: 'normal' | 'rattrapage' | 'mixte';
  total_copies: number;
  prix_unitaire: number;
  bonus: number;
  montant_total: number;
  agent_scolarite_nom: string;
  date_emission: string;
  notes: string;
  enveloppes_details: Array<{
    id: number;
    reference: string;
    matiere_nom: string;
    classes: string;
    nombre_copies: number;
  }>;
}

export interface BudgetBlock {
  copies_deposees: number;
  copies_en_cours: number;
  budget_reel: number;
  budget_estimatif: number;
  nb_enveloppes: number;
}

export interface StatsEnveloppes {
  total: number;
  pretes: number;
  en_correction: number;
  deposees: number;
  facturees: number;
  /** Budget de la session ordinaire (déposées vs estimatif) */
  session_ordinaire?: BudgetBlock;
  /** Budget de la session de rattrapage */
  session_rattrapage?: BudgetBlock;
  /** Budget total (ordinaire + rattrapage) */
  total_budget?: BudgetBlock;
  par_formateur: Array<{
    formateur_nom: string;
    formateur_id: number | null;
    total: number;
    en_correction: number;
    deposees: number;
    copies_total: number;
    montant_du: number;
  }>;
}

export const enveloppesApi = createApi({
  reducerPath: 'enveloppesApi',
  baseQuery: baseQueryWithHeaders,
  tagTypes: ['Enveloppes', 'Recus'],
  endpoints: (builder) => ({
    getEnveloppes: builder.query<Enveloppe[], Record<string, any> | void>({
      query: (params) => ({ url: '/enveloppes/', params: params ?? undefined }),
      transformResponse: (r: any) => Array.isArray(r) ? r : r?.results || [],
      providesTags: ['Enveloppes'],
    }),

    getEnveloppeById: builder.query<Enveloppe, number>({
      query: (id) => `/enveloppes/${id}/`,
      providesTags: (r, e, id) => [{ type: 'Enveloppes', id }],
    }),

    createEnveloppe: builder.mutation<Enveloppe, Partial<Enveloppe>>({
      query: (data) => ({ url: '/enveloppes/', method: 'POST', body: data }),
      invalidatesTags: ['Enveloppes'],
    }),

    retirerEnveloppe: builder.mutation<Enveloppe, { id: number; notes?: string }>({
      query: ({ id, ...data }) => ({ url: `/enveloppes/${id}/retirer/`, method: 'POST', body: data }),
      invalidatesTags: ['Enveloppes'],
    }),

    deposerEnveloppe: builder.mutation<Enveloppe, { id: number; notes?: string }>({
      query: ({ id, ...data }) => ({ url: `/enveloppes/${id}/deposer/`, method: 'POST', body: data }),
      invalidatesTags: ['Enveloppes'],
    }),

    genererDepuisReleves: builder.mutation<{ created: number; message: string }, void>({
      query: () => ({ url: '/enveloppes/generer_depuis_releves/', method: 'POST' }),
      invalidatesTags: ['Enveloppes'],
    }),

    getStatsEnveloppes: builder.query<StatsEnveloppes, void>({
      query: () => '/enveloppes/statistiques/',
      providesTags: ['Enveloppes'],
    }),

    getTableauBord: builder.query<any, void>({
      query: () => '/enveloppes/tableau_bord/',
      providesTags: ['Enveloppes'],
    }),

    deleteEnveloppe: builder.mutation<void, number>({
      query: (id) => ({ url: `/enveloppes/${id}/`, method: 'DELETE' }),
      invalidatesTags: ['Enveloppes'],
    }),

    // Recus
    getRecus: builder.query<RecuCorrection[], void>({
      query: () => '/recus-correction/',
      transformResponse: (r: any) => Array.isArray(r) ? r : r?.results || [],
      providesTags: ['Recus'],
    }),

    changerStatutEnveloppe: builder.mutation<any, { id: number; statut: string }>({
      query: ({ id, statut }) => ({ url: `/enveloppes/${id}/changer_statut/`, method: 'POST', body: { statut } }),
      invalidatesTags: ['Enveloppes'],
    }),

    genererRecu: builder.mutation<RecuCorrection, {
      formateur_id?: number;
      enveloppe_ids?: number[];
      copies_supplementaires?: number;
      copies_supplementaires_motif?: string;
      force_duplicata?: boolean;
    }>({
      query: (data) => ({ url: '/recus-correction/generer_recu/', method: 'POST', body: data }),
      invalidatesTags: ['Recus', 'Enveloppes'],
    }),

    annulerRecu: builder.mutation<any, number>({
      query: (id) => ({ url: `/recus-correction/${id}/annuler_recu/`, method: 'POST' }),
      invalidatesTags: ['Recus', 'Enveloppes'],
    }),

    supprimerDoublonRecu: builder.mutation<{ success: boolean; message: string }, number>({
      query: (id) => ({ url: `/recus-correction/${id}/supprimer_doublon/`, method: 'POST' }),
      invalidatesTags: ['Recus'],
    }),

    detecterDoublonsRecus: builder.query<{
      total_doublons: number;
      groupes: Array<{
        cle: { formateur: string; total_copies: number; nombre_enveloppes: number; matieres: string[] };
        count: number;
        recus: Array<{
          id: number;
          numero_recu: string;
          date_emission: string;
          formateur_nom: string;
          total_copies: number;
          montant_total: number;
          nombre_enveloppes: number;
          matieres: string[];
          agent_nom: string | null;
        }>;
      }>;
    }, void>({
      query: () => '/recus-correction/detecter_doublons/',
      providesTags: ['Recus'],
    }),
  }),
});

export const {
  useGetEnveloppesQuery,
  useGetEnveloppeByIdQuery,
  useCreateEnveloppeMutation,
  useRetirerEnveloppeMutation,
  useDeposerEnveloppeMutation,
  useGenererDepuisRelevesMutation,
  useGetStatsEnveloppesQuery,
  useGetTableauBordQuery,
  useGetRecusQuery,
  useGenererRecuMutation,
  useDeleteEnveloppeMutation,
  useAnnulerRecuMutation,
  useChangerStatutEnveloppeMutation,
  useSupprimerDoublonRecuMutation,
  useDetecterDoublonsRecusQuery,
} = enveloppesApi;
